/**
 * TEMPORARY in-app feedback tool — entry store + auto-sync queue.
 *
 * Entries live in localStorage (the guaranteed copy: survives reloads, works
 * fully offline). Each new report is ALSO POSTed to the feedback collector (the
 * repo endpoint reached over Tailscale) so it lands in feedback-log.jsonl without
 * anyone copy-pasting. If that POST fails (offline, PC off, Tailscale down) the
 * report stays queued and unsynced — nothing is ever lost — and we retry:
 *   - on app start and when connectivity returns (FeedbackSyncRunner),
 *   - when the feedback panel opens,
 *   - and via the "Resend all" button (a manual safety net).
 * The collector de-dupes by id, so resending never doubles a line in the log.
 *
 * Screenshots are big, so the dataURL is NEVER kept in localStorage: the saved
 * entry carries only the filename (screenshots/<id>.png). The image is POSTed and
 * held in memory for this session (so it rides along with a same-session retry
 * and the Download-images fallback); after a restart the report still syncs, just
 * without re-sending an image it no longer has.
 */
import { create } from 'zustand';
import { type FeedbackEntry } from './captureContext';
import { addSynced, postReport, unsyncedEntries, syncCounts } from './sync';

const LS = 'acp.feedback.v1';
const LS_SYNCED = 'acp.feedback.synced.v1';
const LS_DRAFT = 'acp.feedback.draft.v1';

function loadEntries(): FeedbackEntry[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS) : null;
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as FeedbackEntry[]) : [];
  } catch {
    return [];
  }
}
function persistEntries(items: FeedbackEntry[]): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS, JSON.stringify(items));
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}
function loadSynced(): string[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_SYNCED) : null;
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
function persistSynced(ids: string[]): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_SYNCED, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

/**
 * The in-progress (not-yet-saved) report draft. A crash or an accidental close
 * while typing used to lose the words; persisting the draft to localStorage on
 * change and restoring it when the tool reopens means the box comes back with
 * the text intact. This is separate from the saved report queue (LS): the draft
 * only covers the moment before Save is pressed, and is cleared the instant the
 * report is added to the queue.
 */
export interface FeedbackDraft {
  text: string;
  area: string;
}

const EMPTY_DRAFT: FeedbackDraft = { text: '', area: '' };

/** Read back the saved draft (empty text + area if there isn't one). */
export function loadDraft(): FeedbackDraft {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_DRAFT) : null;
    if (!raw) return { ...EMPTY_DRAFT };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return { ...EMPTY_DRAFT };
    const d = parsed as Partial<FeedbackDraft>;
    return {
      text: typeof d.text === 'string' ? d.text : '',
      area: typeof d.area === 'string' ? d.area : '',
    };
  } catch {
    return { ...EMPTY_DRAFT };
  }
}

/**
 * Save the draft (the caller debounces this on keystrokes). A draft that is
 * entirely blank is removed rather than stored, so an emptied box leaves nothing
 * behind to restore next time.
 */
export function persistDraft(draft: FeedbackDraft): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (!draft.text.trim() && !draft.area.trim()) {
      localStorage.removeItem(LS_DRAFT);
      return;
    }
    localStorage.setItem(LS_DRAFT, JSON.stringify({ text: draft.text, area: draft.area }));
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

/** Drop the saved draft — called once a report has been added to the queue. */
export function clearDraft(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(LS_DRAFT);
  } catch {
    /* ignore */
  }
}

/** Trigger a browser download of a single dataURL as <name>. */
function downloadDataUrl(dataUrl: string, name: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

interface FeedbackStoreState {
  entries: FeedbackEntry[];
  /** Ids confirmed written to the collector. */
  syncedIds: string[];
  /** True while a flush/resend is in flight (so the UI can show it, and we don't
   *  run two at once). */
  syncing: boolean;
  /** In-memory only (too big for localStorage): entry id -> screenshot dataURL. */
  images: Record<string, string>;
  add: (entry: FeedbackEntry, screenshotDataUrl?: string) => void;
  clear: () => void;
  /** Push every UNSYNCED report to the collector; mark the ones that land. */
  flush: () => Promise<void>;
  /** Push EVERY report (synced or not) to the collector; it de-dupes by id. */
  resendAll: () => Promise<void>;
  /** { synced, unsynced } counts for the UI. */
  counts: () => { synced: number; unsynced: number };
  /** All entries as JSON Lines (Copy all / Download fallback). */
  exportJsonl: () => string;
  /** Save every in-memory screenshot as <id>.png — the endpoint-down fallback. */
  downloadImages: () => void;
}

/** POST a set of entries, returning the ids that landed. Shared by flush/resend. */
async function pushEntries(
  toSend: FeedbackEntry[],
  images: Record<string, string>,
): Promise<string[]> {
  const landed: string[] = [];
  for (const entry of toSend) {
    const ok = await postReport(entry, images[entry.id]);
    if (ok) landed.push(entry.id);
  }
  return landed;
}

export const useFeedbackStore = create<FeedbackStoreState>((set, get) => ({
  entries: loadEntries(),
  syncedIds: loadSynced(),
  syncing: false,
  images: {},

  add: (entry, screenshotDataUrl) => {
    const entries = [...get().entries, entry];
    persistEntries(entries);
    const images = screenshotDataUrl
      ? { ...get().images, [entry.id]: screenshotDataUrl }
      : get().images;
    set({ entries, images });
    // Fire-and-forget sync; on success record it as synced. On failure it just
    // stays queued for the next flush.
    void postReport(entry, screenshotDataUrl).then((ok) => {
      if (!ok) return;
      const syncedIds = addSynced(get().syncedIds, entry.id);
      persistSynced(syncedIds);
      set({ syncedIds });
    });
  },

  clear: () => {
    persistEntries([]);
    persistSynced([]);
    set({ entries: [], images: {}, syncedIds: [] });
  },

  flush: async () => {
    if (get().syncing) return;
    const toSend = unsyncedEntries(get().entries, get().syncedIds);
    if (toSend.length === 0) return;
    set({ syncing: true });
    try {
      const landed = await pushEntries(toSend, get().images);
      if (landed.length) {
        let syncedIds = get().syncedIds;
        for (const id of landed) syncedIds = addSynced(syncedIds, id);
        persistSynced(syncedIds);
        set({ syncedIds });
      }
    } finally {
      set({ syncing: false });
    }
  },

  resendAll: async () => {
    if (get().syncing) return;
    const toSend = get().entries;
    if (toSend.length === 0) return;
    set({ syncing: true });
    try {
      const landed = await pushEntries(toSend, get().images);
      if (landed.length) {
        let syncedIds = get().syncedIds;
        for (const id of landed) syncedIds = addSynced(syncedIds, id);
        persistSynced(syncedIds);
        set({ syncedIds });
      }
    } finally {
      set({ syncing: false });
    }
  },

  counts: () => syncCounts(get().entries, get().syncedIds),
  exportJsonl: () => get().entries.map((e) => JSON.stringify(e)).join('\n'),
  downloadImages: () => {
    for (const [id, dataUrl] of Object.entries(get().images)) downloadDataUrl(dataUrl, `${id}.png`);
  },
}));
